import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Clock3, CreditCard, FileUp, LoaderCircle, LockKeyhole, ReceiptText, X } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { useStudentPortal } from '../../contexts/StudentPortalContext';
import milestoneService from '../../services/milestone.service';
import storageService from '../../services/storage.service';
import studentPortalService from '../../services/studentPortal.service';
import type { MilestoneListResponse, PaymentMilestone } from '../../types';
import type { AdditionalPayment } from '../../types/studentPortal';
import { portalApiError } from '../../utils/portalApiError';
import { PAYMENT_ACCOUNTS } from '../../data/paymentAccounts';

type PaymentTarget = { kind: 'milestone'; item: PaymentMilestone } | { kind: 'additional'; item: AdditionalPayment };

// Must match the backend (services/payment_proofs.py): MAX_PROOF_FILES files,
// and an "other" method name of at most MAX_OTHER_METHOD_LENGTH characters.
const MAX_PROOF_FILES = 5;
const MAX_OTHER_METHOD_LENGTH = 40;
const MAX_PROOF_SIZE = 20 * 1024 * 1024;
const ALLOWED_PROOF_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];

interface PaymentStatusInfo {
  tone: 'success' | 'info' | 'warning' | 'danger' | 'neutral';
  label: string;
  icon: 'check' | 'clock' | 'card' | 'alert' | 'lock';
}

function getPaymentStatusDetails(status: string, language: 'ar' | 'en'): PaymentStatusInfo {
  const normalized = status.toLowerCase().trim();
  switch (normalized) {
    case 'confirmed':
    case 'paid':
    case 'accepted':
    case 'approved':
      return {
        tone: 'success',
        icon: 'check',
        label: language === 'ar' ? 'تم التأكيد والاعتماد ✓' : 'Confirmed & Accepted ✓',
      };
    case 'submitted':
    case 'payment_submitted':
    case 'under_review':
    case 'pending_review':
    case 'reviewing':
      return {
        tone: 'info',
        icon: 'clock',
        label: language === 'ar' ? 'تم إرسال الإيصال (قيد المراجعة)' : 'Receipt Submitted (Under Review)',
      };
    case 'pending_student':
      return {
        tone: 'warning',
        icon: 'card',
        label: language === 'ar' ? 'يتطلب إرسال الإيصال' : 'Action Required (Pending Proof)',
      };
    case 'pending':
      return {
        tone: 'warning',
        icon: 'card',
        label: language === 'ar' ? 'بانتظار السداد' : 'Pending Payment',
      };
    case 'rejected':
      return {
        tone: 'danger',
        icon: 'alert',
        label: language === 'ar' ? 'مرفوض (يرجى إعادة الإرسال)' : 'Rejected (Resubmission Required)',
      };
    case 'locked':
      return {
        tone: 'neutral',
        icon: 'lock',
        label: language === 'ar' ? 'مغلق' : 'Locked',
      };
    default:
      return {
        tone: 'neutral',
        icon: 'card',
        label: status.replaceAll('_', ' '),
      };
  }
}

function PaymentStateIcon({ icon }: { icon: PaymentStatusInfo['icon'] }) {
  switch (icon) {
    case 'check':
      return <CheckCircle2 size={20} />;
    case 'clock':
      return <Clock3 size={20} />;
    case 'lock':
      return <LockKeyhole size={20} />;
    case 'alert':
      return <AlertCircle size={20} />;
    case 'card':
    default:
      return <CreditCard size={20} />;
  }
}

export default function PortalPayments() {
  const { language } = useI18n();
  const { workspace, refresh } = useStudentPortal();
  const [milestones, setMilestones] = useState<MilestoneListResponse | null>(null);
  const [additional, setAdditional] = useState<AdditionalPayment[]>([]);
  const [target, setTarget] = useState<PaymentTarget | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const applicationId = workspace?.application?.id;

  const load = useCallback(async () => {
    if (!applicationId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const results = await Promise.allSettled([
      milestoneService.getApplicationMilestones(applicationId),
      studentPortalService.getAdditionalPayments(),
    ]);
    if (results[0].status === 'fulfilled') setMilestones(results[0].value);
    if (results[1].status === 'fulfilled') setAdditional(results[1].value);
    if (results.every((result) => result.status === 'rejected')) {
      setError(
        language === 'ar'
          ? 'تعذر تحميل بيانات الدفع.'
          : 'Could not load payment information.'
      );
    }
    setLoading(false);
  }, [applicationId, language]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = useMemo(
    () => ({
      total: milestones?.total_amount || 0,
      paid: milestones?.paid_amount || 0,
      remaining: milestones?.remaining_amount || 0,
    }),
    [milestones]
  );

  if (loading) {
    return (
      <div className="portal-card portal-empty">
        <LoaderCircle className="portal-spin" />
      </div>
    );
  }

  if (!applicationId) {
    return (
      <div className="portal-page">
        <div className="portal-page-heading">
          <div>
            <h1>{language === 'ar' ? 'المدفوعات' : 'Payments'}</h1>
          </div>
        </div>
        <div className="portal-card portal-empty">
          <div className="portal-empty-icon">
            <ReceiptText />
          </div>
          <h3>{language === 'ar' ? 'لا يوجد طلب للدفع' : 'There is no application to pay for'}</h3>
          <p>
            {language === 'ar'
              ? 'اختر وجهتك وأكمل الإعداد ثم وافق على عرض السعر أولًا.'
              : 'Choose your destination, complete setup and accept your quotation first.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-page">
      <div className="portal-page-heading">
        <div>
          <span className="portal-eyebrow">
            <CreditCard size={14} />
            {language === 'ar' ? 'سجل مالي واضح' : 'Clear payment record'}
          </span>
          <h1>{language === 'ar' ? 'المدفوعات والفواتير' : 'Payments & fees'}</h1>
          <p>
            {language === 'ar'
              ? 'كل دفعة خدمة أو رسوم إضافية موثقة بحالتها وإثباتها.'
              : 'Every service instalment and additional fee is documented with its status and proof.'}
          </p>
        </div>
      </div>

      {error && (
        <div className="portal-alert portal-alert-error">
          <AlertCircle size={20} />
          <div>
            <strong>
              {language === 'ar' ? 'تعذر تحميل المدفوعات' : 'Payments could not be loaded'}
            </strong>
            <p>{error}</p>
          </div>
        </div>
      )}

      <section className="portal-grid portal-grid-4">
        <PaymentStat
          label={language === 'ar' ? 'إجمالي الخدمة' : 'Service total'}
          value={totals.total}
          currency={milestones?.milestones[0]?.currency}
          icon={<ReceiptText />}
          primary
        />
        <PaymentStat
          label={language === 'ar' ? 'تم تأكيده' : 'Confirmed'}
          value={totals.paid}
          currency={milestones?.milestones[0]?.currency}
          icon={<CheckCircle2 />}
        />
        <PaymentStat
          label={language === 'ar' ? 'المتبقي' : 'Remaining'}
          value={totals.remaining}
          currency={milestones?.milestones[0]?.currency}
          icon={<Clock3 />}
        />
        <PaymentStat
          label={language === 'ar' ? 'رسوم إضافية' : 'Additional fees'}
          value={additional.filter((item) => item.status === 'pending_student').length}
          icon={<CreditCard />}
          plain
        />
      </section>

      <section className="portal-grid portal-grid-main" style={{ marginTop: 16 }}>
        <article className="portal-card">
          <div className="portal-card-header">
            <h2>{language === 'ar' ? 'جدول دفعات الخدمة' : 'Service payment schedule'}</h2>
            <span className="portal-badge neutral">{milestones?.total_milestones || 0}</span>
          </div>
          <div className="portal-card-body portal-payment-list">
            {milestones?.milestones.length ? (
              milestones.milestones.map((item) => {
                const statusInfo = getPaymentStatusDetails(item.status, language);
                const canPay = ['pending', 'rejected'].includes(item.status);
                return (
                  <div key={item.id} className="portal-payment-row">
                    <span className={`portal-payment-state ${statusInfo.tone}`}>
                      <PaymentStateIcon icon={statusInfo.icon} />
                    </span>
                    <span className="portal-payment-copy">
                      <strong>{language === 'ar' ? (item.title_ar || item.title) : item.title}</strong>
                      <small>{language === 'ar' ? (item.description_ar || item.description) : item.description}</small>
                      <span className={`portal-badge ${statusInfo.tone}`}>
                        {statusInfo.label}
                      </span>
                    </span>
                    <b className="portal-payment-amount">
                      {Number(item.amount).toLocaleString()} <small>{item.currency}</small>
                    </b>
                    {canPay && (
                      <button
                        type="button"
                        className="portal-button portal-button-secondary"
                        onClick={() => setTarget({ kind: 'milestone', item })}
                      >
                        {item.status === 'rejected'
                          ? (language === 'ar' ? 'إعادة الإرسال' : 'Resubmit')
                          : (language === 'ar' ? 'إرسال الدفع' : 'Submit payment')}
                      </button>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="portal-empty">
                <p>
                  {language === 'ar'
                    ? 'لا يوجد جدول دفع مهيأ لهذا الطلب.'
                    : 'No payment schedule is configured for this application.'}
                </p>
              </div>
            )}
          </div>
        </article>

        <aside className="portal-card">
          <div className="portal-card-header">
            <h2>{language === 'ar' ? 'الرسوم الإضافية' : 'Additional fees'}</h2>
            {additional.some((item) => item.status === 'pending_student') && (
              <span className="portal-badge warning">
                {language === 'ar' ? 'يتطلب إجراء' : 'Action required'}
              </span>
            )}
          </div>
          <div className="portal-card-body portal-side-payments">
            {additional.length ? (
              additional.map((item) => {
                const statusInfo = getPaymentStatusDetails(item.status, language);
                return (
                  <div key={item.id} className="portal-side-payment">
                    <div>
                      <span className={`portal-badge ${statusInfo.tone}`}>{statusInfo.label}</span>
                      <h3>{language === 'ar' ? (item.title_ar || item.title) : item.title}</h3>
                      <p>{language === 'ar' ? (item.description_ar || item.description) : item.description}</p>
                    </div>
                    <div className="portal-mini-row">
                      <span>
                        {item.payee_type === 'third_party'
                          ? (language === 'ar' ? 'جهة خارجية' : 'Third party')
                          : 'EduNasser'}
                      </span>
                      <strong>
                        {Number(item.amount).toLocaleString()} {item.currency}
                      </strong>
                    </div>
                    {item.supporting_document_url && (
                      <a
                        href={item.supporting_document_url}
                        target="_blank"
                        rel="noreferrer"
                        className="portal-text-link"
                      >
                        {language === 'ar' ? 'عرض المستند الداعم' : 'View supporting document'}
                      </a>
                    )}
                    {item.status === 'pending_student' && (
                      <button
                        type="button"
                        className="portal-button portal-button-primary portal-button-full"
                        onClick={() => setTarget({ kind: 'additional', item })}
                      >
                        {language === 'ar' ? 'إرسال إثبات الدفع' : 'Submit payment proof'}
                      </button>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="portal-empty">
                <div className="portal-empty-icon">
                  <CheckCircle2 />
                </div>
                <h3>{language === 'ar' ? 'لا توجد رسوم إضافية' : 'No additional fees'}</h3>
                <p>
                  {language === 'ar'
                    ? 'سيظهر هنا فقط ما يطلبه المستشار وتعتمده الإدارة.'
                    : 'Only consultant requests approved by an administrator appear here.'}
                </p>
              </div>
            )}
          </div>
        </aside>
      </section>

      {target && (
        <PaymentDialog
          target={target}
          language={language}
          onClose={() => setTarget(null)}
          onComplete={async () => {
            setTarget(null);
            await load();
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function PaymentStat({
  label,
  value,
  currency,
  icon,
  primary,
  plain,
}: {
  label: string;
  value: number;
  currency?: string;
  icon: ReactNode;
  primary?: boolean;
  plain?: boolean;
}) {
  return (
    <article className={`portal-card portal-stat ${primary ? 'portal-stat-primary' : ''}`}>
      <div className="portal-stat-top">
        <div>
          <strong>
            {plain ? value : Number(value).toLocaleString()}{' '}
            {!plain && <small>{currency || ''}</small>}
          </strong>
          <p>{label}</p>
        </div>
        <span className="portal-stat-icon">{icon}</span>
      </div>
    </article>
  );
}

function PaymentDialog({
  target,
  language,
  onClose,
  onComplete,
}: {
  target: PaymentTarget;
  language: 'en' | 'ar';
  onClose: () => void;
  onComplete: () => Promise<void>;
}) {
  const [method, setMethod] = useState('instapay');
  const [methodOther, setMethodOther] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const item = target.item;
  const title = language === 'ar' ? (item.title_ar || item.title) : item.title;
  const isOther = method === 'other';
  const otherName = methodOther.trim().replace(/\s+/g, ' ');
  const canAddMore = files.length < MAX_PROOF_FILES;

  const openPicker = () => fileInputRef.current?.click();

  // Validates each picked file, skips duplicates and anything over the limit,
  // and keeps the ones that pass. One bad file never discards the good ones.
  const handleFilesPicked = (picked: FileList | null) => {
    if (!picked || picked.length === 0) return;
    const problems: string[] = [];
    const next = [...files];
    for (const candidate of Array.from(picked)) {
      if (!ALLOWED_PROOF_TYPES.includes(candidate.type)) {
        problems.push(
          language === 'ar'
            ? `«${candidate.name}»: يرجى رفع صورة (JPG, PNG) أو ملف PDF`
            : `“${candidate.name}”: please upload an image (JPG, PNG) or a PDF`
        );
        continue;
      }
      if (candidate.size > MAX_PROOF_SIZE) {
        problems.push(
          language === 'ar'
            ? `«${candidate.name}»: حجم الملف يجب ألا يتجاوز 20 ميجابايت`
            : `“${candidate.name}”: the file must not exceed 20 MB`
        );
        continue;
      }
      const duplicate = next.some(
        (existing) =>
          existing.name === candidate.name &&
          existing.size === candidate.size &&
          existing.lastModified === candidate.lastModified
      );
      if (duplicate) continue;
      if (next.length >= MAX_PROOF_FILES) {
        problems.push(
          language === 'ar'
            ? `يمكنك إرفاق ${MAX_PROOF_FILES} ملفات كحد أقصى`
            : `You can attach up to ${MAX_PROOF_FILES} files`
        );
        break;
      }
      next.push(candidate);
    }
    setFiles(next);
    setError(problems.length ? problems.join(' · ') : null);
    // Cleared so picking the same file again after removing it still fires.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setFiles((current) => current.filter((_, position) => position !== index));
    setError(null);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (files.length === 0) {
      setError(
        language === 'ar'
          ? 'يرجى اختيار إيصال الدفع أولاً'
          : 'Please select the payment receipt first'
      );
      return;
    }
    if (isOther && otherName.length < 2) {
      setError(
        language === 'ar'
          ? 'يرجى كتابة طريقة الدفع التي استخدمتها'
          : 'Please enter the payment method you used'
      );
      return;
    }

    setSaving(true);
    setError(null);
    try {
      // One at a time: clearer progress, and gentler on a mobile connection.
      const proofs: string[] = [];
      for (let index = 0; index < files.length; index += 1) {
        setProgress({ done: index, total: files.length });
        const stored = (await storageService.uploadFile(files[index], 'payment_proofs')) as Record<string, string>;
        const url = stored.public_url || stored.file_url || stored.url;
        if (!url) throw new Error('Uploaded proof URL was not returned');
        proofs.push(url);
      }
      setProgress({ done: files.length, total: files.length });

      if (target.kind === 'milestone') {
        await milestoneService.submitMilestonePayment(
          target.item.application_id,
          target.item.id,
          {
            payment_method: method,
            payment_method_other: isOther ? otherName : undefined,
            payment_reference: '0000',
            payment_proof_url: proofs[0],
            payment_proof_urls: proofs,
          }
        );
      } else {
        await studentPortalService.submitAdditionalPayment(
          target.item.id,
          {
            method,
            method_other: isOther ? otherName : undefined,
            reference: '0000',
            proof_file_url: proofs[0],
            proof_file_urls: proofs,
            idempotency_key: crypto.randomUUID(),
          }
        );
      }
      await onComplete();
    } catch (requestError: unknown) {
      setError(
        portalApiError(requestError, language, {
          ar: 'تعذر إرسال إثبات الدفع. لم تُسجّل العملية ويمكنك المحاولة مرة أخرى.',
          en: 'Could not submit the payment proof. Nothing was recorded; you can try again.',
        })
      );
    } finally {
      setSaving(false);
      setProgress(null);
    }
  };

  return (
    <div className="portal-dialog-backdrop" role="presentation">
      <div className="portal-dialog" role="dialog" aria-modal="true">
        <div className="portal-dialog-head">
          <div>
            <span className="portal-eyebrow">
              <CreditCard size={14} />
              {language === 'ar' ? 'إثبات الدفع' : 'Payment proof'}
            </span>
            <h2>{title}</h2>
          </div>
          <button
            type="button"
            className="portal-icon-button"
            onClick={onClose}
            aria-label={language === 'ar' ? 'إغلاق' : 'Close'}
          >
            <X />
          </button>
        </div>

        <form onSubmit={submit} className="portal-dialog-body">
          {error && (
            <div className="portal-alert portal-alert-error">
              <AlertCircle size={18} />
              <div>
                <strong>
                  {language === 'ar'
                    ? 'لم يتم إرسال إثبات الدفع'
                    : 'Payment proof was not submitted'}
                </strong>
                <p>{error}</p>
              </div>
            </div>
          )}

          {/* Payment Account Details */}
          <div
            style={{
              background: '#fbfafc',
              border: '1px solid #e9e4f0',
              borderRadius: '16px',
              padding: '12px',
              display: 'grid',
              gap: '8px',
            }}
          >
            <div
              style={{
                fontSize: '12px',
                fontWeight: 'bold',
                color: '#5B3FD0',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span>💳</span>
              <span>{language === 'ar' ? 'معلومات التحويل' : 'Payment Information'}</span>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '8px',
              }}
            >
              <div
                style={{
                  background: '#fff',
                  border: '1px solid #f0edf5',
                  borderRadius: '10px',
                  padding: '8px 10px',
                }}
              >
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: '#E60000',
                    marginBottom: '4px',
                  }}
                >
                  {PAYMENT_ACCOUNTS.vodafoneCash.icon}{' '}
                  {language === 'ar'
                    ? PAYMENT_ACCOUNTS.vodafoneCash.nameAr
                    : PAYMENT_ACCOUNTS.vodafoneCash.nameEn}
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '12px',
                  }}
                >
                  <span style={{ color: '#707070', fontSize: '11px' }}>
                    {language === 'ar' ? 'الرقم' : 'Number'}
                  </span>
                  <span
                    style={{
                      fontFamily: 'monospace',
                      fontWeight: '700',
                      direction: 'ltr',
                    }}
                  >
                    {PAYMENT_ACCOUNTS.vodafoneCash.number}
                  </span>
                </div>
              </div>
              <div
                style={{
                  background: '#fff',
                  border: '1px solid #f0edf5',
                  borderRadius: '10px',
                  padding: '8px 10px',
                }}
              >
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: '#5B3FD0',
                    marginBottom: '4px',
                  }}
                >
                  {PAYMENT_ACCOUNTS.instaPay.icon}{' '}
                  {language === 'ar'
                    ? PAYMENT_ACCOUNTS.instaPay.nameAr
                    : PAYMENT_ACCOUNTS.instaPay.nameEn}
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '12px',
                    marginBottom: '2px',
                  }}
                >
                  <span style={{ color: '#707070', fontSize: '11px' }}>
                    {language === 'ar' ? 'الرقم' : 'Number'}
                  </span>
                  <span
                    style={{
                      fontFamily: 'monospace',
                      fontWeight: '700',
                      direction: 'ltr',
                    }}
                  >
                    {PAYMENT_ACCOUNTS.instaPay.number}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '12px',
                  }}
                >
                  <span style={{ color: '#707070', fontSize: '11px' }}>
                    {language === 'ar' ? 'الاسم' : 'Name'}
                  </span>
                  <span style={{ fontWeight: '700' }} dir="rtl">
                    {PAYMENT_ACCOUNTS.instaPay.recipientName}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="portal-field">
            <label>{language === 'ar' ? 'طريقة الدفع' : 'Payment method'}</label>
            <select
              className="portal-select"
              value={method}
              onChange={(event) => setMethod(event.target.value)}
              disabled={saving}
            >
              <option value="instapay">{language === 'ar' ? 'إنستاباي' : 'InstaPay'}</option>
              <option value="vodafone_cash">{language === 'ar' ? 'فودافون كاش' : 'Vodafone Cash'}</option>
              <option value="bank_transfer">{language === 'ar' ? 'تحويل بنكي' : 'Bank transfer'}</option>
              <option value="cash">{language === 'ar' ? 'نقدي' : 'Cash'}</option>
              <option value="card">{language === 'ar' ? 'بطاقة' : 'Card'}</option>
              <option value="other">{language === 'ar' ? 'طريقة أخرى' : 'Other method'}</option>
            </select>
          </div>

          {/* Which "other" method - required, so the reviewer knows what to check */}
          {isOther && (
            <div className="portal-field">
              <label htmlFor="payment-method-other">
                {language === 'ar' ? 'ما هي طريقة الدفع التي استخدمتها؟' : 'Which payment method did you use?'}
              </label>
              <input
                id="payment-method-other"
                className="portal-input"
                value={methodOther}
                onChange={(event) => setMethodOther(event.target.value)}
                maxLength={MAX_OTHER_METHOD_LENGTH}
                placeholder={language === 'ar' ? 'مثال: فوري، ويسترن يونيون، إيداع بنكي' : 'e.g. Fawry, Western Union, bank deposit'}
                disabled={saving}
                autoFocus
                required
              />
            </div>
          )}

          {/* Hidden File Input - several files at once */}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/png,image/jpeg,application/pdf"
            multiple
            style={{ display: 'none' }}
            onChange={(event) => handleFilesPicked(event.target.files)}
          />

          {/* Selected files, or the picker when none are chosen yet */}
          {files.length > 0 ? (
            <div style={{ display: 'grid', gap: 8 }}>
              {files.map((file, index) => (
                <div key={`${file.name}-${file.size}-${file.lastModified}`} className="portal-file-staged">
                  <div className="portal-file-staged-main">
                    <div className="portal-file-staged-icon">
                      <ReceiptText size={22} />
                    </div>
                    <div className="portal-file-staged-details">
                      <div className="portal-file-staged-title-row">
                        <span className="portal-badge info" style={{ minHeight: 22, fontSize: 10, padding: '0 8px' }}>
                          {language === 'ar' ? `ملف ${index + 1}` : `File ${index + 1}`}
                        </span>
                        <strong className="portal-file-staged-name" title={file.name}>
                          {file.name}
                        </strong>
                      </div>
                      <span className="portal-file-staged-size">
                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                      </span>
                    </div>
                  </div>
                  <div className="portal-file-staged-actions">
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => removeFile(index)}
                      className="portal-button portal-button-danger portal-button-sm"
                      aria-label={language === 'ar' ? `إزالة ${file.name}` : `Remove ${file.name}`}
                    >
                      <X size={14} />
                      <span>{language === 'ar' ? 'إزالة' : 'Remove'}</span>
                    </button>
                  </div>
                </div>
              ))}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span className="portal-field-hint">
                  {language === 'ar'
                    ? `${files.length} من ${MAX_PROOF_FILES} ملفات`
                    : `${files.length} of ${MAX_PROOF_FILES} files`}
                </span>
                {canAddMore && (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={openPicker}
                    className="portal-button portal-button-secondary portal-button-sm"
                  >
                    <FileUp size={14} />
                    <span>{language === 'ar' ? 'إضافة ملف آخر' : 'Add another file'}</span>
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div
              role="button"
              tabIndex={0}
              onClick={openPicker}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openPicker();
                }
              }}
              className="portal-file-drop"
            >
              <FileUp size={24} />
              <strong>
                {language === 'ar' ? 'اضغط لاختيار صورة أو ملف إثبات الدفع' : 'Choose payment receipt files'}
              </strong>
              <small>
                {language === 'ar'
                  ? `يمكنك اختيار حتى ${MAX_PROOF_FILES} ملفات · PDF, JPG, PNG · الحد الأقصى 20 ميجابايت للملف`
                  : `Up to ${MAX_PROOF_FILES} files · PDF, JPG, PNG · Max 20 MB each`}
              </small>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="portal-button portal-button-primary portal-button-full"
            disabled={saving || files.length === 0 || (isOther && otherName.length < 2)}
          >
            {saving ? (
              <>
                <LoaderCircle size={17} className="portal-spin" />
                <span>
                  {progress && progress.done < progress.total
                    ? language === 'ar'
                      ? `جاري رفع الملف ${progress.done + 1} من ${progress.total}...`
                      : `Uploading file ${progress.done + 1} of ${progress.total}...`
                    : language === 'ar'
                      ? 'جاري الإرسال للمراجعة...'
                      : 'Submitting for review...'}
                </span>
              </>
            ) : (
              <>
                <FileUp size={17} />
                <span>
                  {language === 'ar' ? 'رفع وإرسال للمراجعة' : 'Upload and submit for review'}
                </span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
